import type { ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn, formatBytes, formatDate, humanise } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Resume, TernaryAnswer } from '@/types/api'

/**
 * A user's profile, read only: everything their applications are filled
 * from, laid out in the order a form asks for it. The same record the user
 * sees in their own app, so "why did it answer that?" can be answered here.
 *
 * No credential is part of it: not the account password, not the application
 * password (the side column says only whether one is set), not a portal login.
 */
export function ProfilePanel({ userId, email }: { userId: string; email: string }) {
  const query = useQuery({ queryKey: ['user-profile', userId], queryFn: () => adminApi.userProfile(userId), staleTime: 30_000 })

  if (query.isLoading) {
    return (
      <div className="card space-y-3 p-5">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }
  if (query.isError || !query.data) {
    return (
      <div className="card">
        <ErrorState title="The profile could not be loaded" onRetry={() => void query.refetch()} />
      </div>
    )
  }
  const { profile, resumes } = query.data
  if (!profile) {
    return (
      <div className="space-y-5">
        <div className="card">
          <EmptyState title="No profile yet" description="This user has not filled in their profile or uploaded a resume." />
        </div>
        <Resumes userId={userId} resumes={resumes} />
      </div>
    )
  }

  const { location, contact, checklist, jobPreference: wants, workEligibility } = profile
  const money = (value: number | null, currency: string | null) =>
    value === null ? null : new Intl.NumberFormat('en', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(value)
  const address = location
    ? [location.addressLine1, location.addressLine2, location.city, location.countyDistrict, location.stateName, location.postalCode, location.countryName].filter(Boolean).join(', ')
    : null

  return (
    <div className="space-y-5">
      <Block title="About">
        <Grid>
          <Item label="Full name" value={profile.fullName} />
          <Item label="Email" value={email} mono />
          <Item label="Phone" value={contact?.phoneNumber} mono />
          <Item label="Date of birth" value={profile.dateOfBirth ? formatDate(profile.dateOfBirth) : null} />
          <Item label="Current title" value={profile.currentTitle} />
          <Item label="Current company" value={profile.currentCompany} />
          <Item label="Experience" value={profile.totalExperienceYears === null ? null : `${profile.totalExperienceYears} years`} />
          <Item label="Notice period" value={profile.noticePeriodDays === null ? null : `${profile.noticePeriodDays} days`} />
          <Item label="Current salary" value={money(profile.currentSalary, profile.salaryCurrency)} />
          <Item label="Expected salary" value={money(profile.expectedSalary, profile.salaryCurrency)} />
        </Grid>
        {profile.headline && <Long label="Headline">{profile.headline}</Long>}
        {profile.summary && <Long label="Summary">{profile.summary}</Long>}
      </Block>

      <Block title="Location and links">
        <Grid>
          <Item label="Address" value={address} wide />
          <Item label="LinkedIn" value={contact?.linkedinUrl} link />
          <Item label="GitHub" value={contact?.githubUrl} link />
          <Item label="Portfolio" value={contact?.portfolioUrl} link />
        </Grid>
      </Block>

      <Resumes userId={userId} resumes={resumes} />

      <Block title="Work history" count={profile.experiences.length}>
        {profile.experiences.length === 0 ? (
          <None>No roles on the profile.</None>
        ) : (
          <ol className="space-y-4">
            {profile.experiences.map((role) => (
              <li key={role.id} className="border-l-2 border-line pl-4">
                <p className="text-[14px] font-medium text-ink">
                  {role.title} <span className="font-normal text-ink-muted">at {role.company}</span>
                </p>
                <p className="num mt-0.5 text-[12px] text-ink-muted">
                  {[`${role.startDate ?? '?'} to ${role.isCurrent ? 'now' : (role.endDate ?? '?')}`, role.location, role.source === 'resume' ? 'from the resume' : 'typed in'].filter(Boolean).join(' · ')}
                </p>
                {role.description && <p className="mt-1.5 whitespace-pre-line text-[13px] leading-relaxed text-ink-soft">{role.description}</p>}
              </li>
            ))}
          </ol>
        )}
      </Block>

      <Block title="Skills" count={profile.skills.length}>
        {profile.skills.length === 0 ? (
          <None>No skills on the profile.</None>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {profile.skills.map((skill) => (
              <li
                key={skill.id}
                className={cn('inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-[12.5px]', skill.isPrimary ? 'border-brand/30 bg-brand-soft text-brand' : 'border-line bg-surface-muted text-ink-soft')}
              >
                {skill.name}
                {skill.yearsOfExperience !== null && <span className="num text-[11.5px] opacity-80">{skill.yearsOfExperience}y</span>}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title="Education">
        <Grid>
          <Item label="University" value={profile.educationUniversity} />
          <Item label="Course" value={profile.educationCourse} />
          <Item label="Graduated" value={profile.educationGraduationYear} />
        </Grid>
      </Block>

      <Block title="Work eligibility">
        {!workEligibility || workEligibility.workAuthorizations.length === 0 ? (
          <None>Nothing given.</None>
        ) : (
          <ul className="divide-y divide-line">
            {workEligibility.workAuthorizations.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5 first:pt-0 last:pb-0">
                <span className="min-w-[120px] text-[13.5px] font-medium text-ink">{row.countryName}</span>
                {row.isCitizenship && <StatusPill tone="brand" size="sm">Citizen</StatusPill>}
                {row.legallyAuthorized !== null && (
                  <StatusPill tone={row.legallyAuthorized ? 'success' : 'neutral'} size="sm">
                    {row.legallyAuthorized ? 'Authorised to work' : 'Not authorised'}
                  </StatusPill>
                )}
                {row.requiresSponsorship !== null && (
                  <StatusPill tone={row.requiresSponsorship ? 'warning' : 'neutral'} size="sm">
                    {row.requiresSponsorship ? 'Needs sponsorship' : 'No sponsorship needed'}
                  </StatusPill>
                )}
                {row.authorizationBasis && <span className="text-[12.5px] text-ink-muted">{humanise(row.authorizationBasis)}</span>}
                {row.visaExpiresOn && <span className="num text-[12.5px] text-ink-muted">until {row.visaExpiresOn}</span>}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title="What they are looking for">
        {!wants ? (
          <None>No preferences set.</None>
        ) : (
          <Grid>
            <Item label="Titles" value={list(wants.desiredTitles)} wide />
            <Item label="Locations" value={list(wants.desiredLocations)} wide />
            <Item label="Workplace" value={list(wants.workplaceTypes.map(humanise))} />
            <Item label="Employment" value={list(wants.employmentTypes.map(humanise))} />
            <Item label="Salary range" value={wants.minSalary === null && wants.maxSalary === null ? null : `${money(wants.minSalary, wants.salaryCurrency) ?? 'any'} to ${money(wants.maxSalary, wants.salaryCurrency) ?? 'any'}`} />
            <Item label="Experience range" value={wants.minExperienceYears === null && wants.maxExperienceYears === null ? null : `${wants.minExperienceYears ?? 0} to ${wants.maxExperienceYears ?? 'any'} years`} />
            <Item label="Industries" value={list(wants.industries)} wide />
            <Item label="Target companies" value={list(wants.targetCompanies)} wide />
            <Item label="Excluded companies" value={list(wants.excludedCompanies)} wide />
            <Item label="Excluded keywords" value={list(wants.excludedKeywords)} wide />
            <Item label="Needs visa sponsorship" value={yesNo(wants.requiresVisaSponsorship)} />
            <Item label="Open to remote anywhere" value={yesNo(wants.openToRemoteWorldwide)} />
          </Grid>
        )}
      </Block>

      <Block title="Standard answers" description="What the user chose for the questions most forms ask.">
        {!checklist ? (
          <None>Not answered yet.</None>
        ) : (
          <>
            <Grid>
              <Item label="Open to working in person" value={yesNo(checklist.openToInPerson)} />
              <Item label="Willing to relocate" value={yesNo(checklist.willingToRelocate)} />
              <Item label="Can start immediately" value={yesNo(checklist.canStartImmediately)} />
              <Item label="Reliable transportation" value={yesNo(checklist.hasReliableTransportation)} />
              <Item label="Government clearance" value={checklist.hasGovernmentClearance ? (checklist.clearanceDetails || 'Yes') : 'No'} />
              <Item label="Foreign government ties" value={yesNo(checklist.hasForeignGovernmentTies)} />
              <Item label="Needs accommodations" value={ternary(checklist.needsAccommodations)} />
              <Item label="Gender" value={checklist.gender} />
              <Item label="Race or ethnicity" value={checklist.raceEthnicity} />
              <Item label="Veteran" value={ternary(checklist.veteranStatus)} />
              <Item label="Disability" value={ternary(checklist.disabilityStatus)} />
            </Grid>
            {checklist.accommodationDetails && <Long label="Accommodation details">{checklist.accommodationDetails}</Long>}
            {checklist.additionalNotes && <Long label="Notes for applications">{checklist.additionalNotes}</Long>}
          </>
        )}
      </Block>

      <p className="px-1 text-[12px] text-ink-faint">Profile last changed {formatDate(profile.updatedAt)}. Read only: the user edits it in their own app.</p>
    </div>
  )
}

const list = (values: string[]): string | null => (values.length ? values.join(', ') : null)
const yesNo = (value: boolean): string => (value ? 'Yes' : 'No')
const ternary = (value: TernaryAnswer): string => ({ yes: 'Yes', no: 'No', prefer_not_to_say: 'Prefers not to say' })[value] ?? humanise(value)

function Block({ title, description, count, children }: { title: string; description?: string; count?: number; children: ReactNode }) {
  return (
    <section className="card overflow-hidden">
      <header className="border-b border-line px-4 py-3 sm:px-5">
        <h2 className="text-[15px] font-semibold text-ink">
          {title}
          {count !== undefined && count > 0 && <span className="num ml-2 text-[12.5px] font-medium text-ink-muted">{count}</span>}
        </h2>
        {description && <p className="mt-0.5 text-[12.5px] text-ink-muted">{description}</p>}
      </header>
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  )
}

function Grid({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2">{children}</dl>
}

function Item({ label, value, mono, link, wide }: { label: string; value: string | number | null | undefined; mono?: boolean; link?: boolean; wide?: boolean }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className={cn('mt-0.5 break-words text-[14px]', empty ? 'text-ink-faint' : 'text-ink', mono && !empty && 'num text-[13px]')}>
        {empty ? (
          'Not given'
        ) : link ? (
          <a href={String(value)} target="_blank" rel="noreferrer noopener" className="break-all text-info hover:underline">
            {String(value).replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  )
}

function Long({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-4 border-t border-line pt-3.5">
      <p className="text-[12px] text-ink-muted">{label}</p>
      <p className="mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-soft">{children}</p>
    </div>
  )
}

function None({ children }: { children: ReactNode }) {
  return <p className="text-[13.5px] text-ink-muted">{children}</p>
}

function Resumes({ userId, resumes }: { userId: string; resumes: Resume[] }) {
  const download = useMutation({
    mutationFn: (resume: Resume) => adminApi.downloadResume(userId, resume.id, resume.fileName),
    onError: () => toast.error('The file could not be downloaded'),
  })
  return (
    <Block title="Resumes" count={resumes.length}>
      {resumes.length === 0 ? (
        <None>No resume uploaded.</None>
      ) : (
        <ul className="divide-y divide-line">
          {resumes.map((resume) => (
            <li key={resume.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[14px] font-medium text-ink">{resume.fileName}</span>
                  {resume.isDefault && <StatusPill tone="brand" size="sm">Default</StatusPill>}
                </p>
                <p className="num mt-0.5 truncate text-[12px] text-ink-muted">
                  {[formatBytes(resume.sizeBytes), humanise(resume.status), `uploaded ${formatDate(resume.createdAt)}`].join(' · ')}
                </p>
                {resume.parseError && <p className="mt-0.5 truncate text-[12px] text-danger">{resume.parseError}</p>}
              </div>
              <Button variant="secondary" size="xs" loading={download.isPending && download.variables?.id === resume.id} onClick={() => download.mutate(resume)} className="shrink-0">
                Download
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Block>
  )
}
